// RITA-1520 (Nutzungserfassung Phase 3b, Epic RITA-1496) — Totklick-Erkennung.
//
// Definition (Ticket RITA-1500, Entwurf v2 nach Prüfer-Panel): ein Totklick ist ein Klick auf
// etwas, das bedienbar AUSSIEHT, es aber nicht ist. Erkannt wird SYNCHRON aus dem DOM am
// Klickpunkt — ohne Beobachter und ohne Timer. Die zunächst erwogene Heuristik „hat sich in
// 400 ms nichts geändert“ ist gestrichen: framer-motion, TouchRipple, Tooltips und das Grid
// mutieren fast immer, die Messung wäre strukturell leer, und ein Dauerbeobachter widerspräche
// dem Versprechen „passiver Zuhörer“.
//
// Datenschutz: Der Schlüssel besteht AUSSCHLIESSLICH aus dem geprüften Marker eines Vorfahren
// ([data-usage], eine feste Wortliste aus usageKeys.ts) und aus Tag-Namen. Nie id, nie
// data-testid, nie Text — anders als beim Klick-Schlüssel (clickTarget.ts) gibt es hier kein
// bedienbares Element, dessen technische Kennung fachlich etwas bedeutete; eine Zeilenkennung
// wie 'agent-row-42' wäre dagegen ein Personenbezug. Deshalb wird `sanitizeIdent` hier bewusst
// NICHT verwendet: dessen '#' für Ziffernfolgen steht gar nicht im Namensmuster des Backends.
import { elementKind, INTERACTIVE_SELECTOR, USAGE_ATTRIBUTE } from './clickTarget';

/** Ergebnis: technischer Ort des Totklicks und die Art des Elements, das bedienbar aussah. */
export interface DeadClick {
  key: string;
  kind: string;
}

export interface DeadClickPoint {
  clientX: number;
  clientY: number;
}

export interface DeadClickDeps {
  /** Ersetzbar für Tests; sonst getComputedStyle des Fensters. */
  computedStyle?: (element: Element) => { cursor: string };
}

/** Grenze aus batchSchema.ts (propString(16)) — dieselbe wie beim Klick-Ereignis. */
const MAX_KIND_LENGTH = 16;
/** Höchstens so viele Tag-Stufen ohne bzw. mit markiertem Vorfahren. */
const MAX_TAG_LEVELS = 3;
const MAX_TAG_LEVELS_WITH_MARKER = 2;
/**
 * So viele deaktivierte Nachfahren werden vermessen. getBoundingClientRect erzwingt ein Layout;
 * ein Klick darf die Bedienung nicht bremsen, und mehr als eine Handvoll deaktivierter Elemente
 * liegt ohnehin nie unter einem Klickpunkt.
 */
const MAX_DISABLED_CANDIDATES = 20;

const DISABLED_SELECTOR = '[disabled], [aria-disabled="true"]';
/** Eingaben sind keine Totklicks: dort ist der Klick der Anfang einer Eingabe. */
const INPUT_SELECTOR = 'input, textarea, select, [contenteditable]';
/**
 * Der Dialog-Hintergrund von MUI SCHLIESST den Dialog — der Klick wirkt also, er ist Bedienung.
 * Die zweite Schreibweise fängt die zusammengesetzten Klassennamen ab (MuiBackdrop-invisible).
 */
const BACKDROP_SELECTOR = '.MuiBackdrop-root, [class*="MuiBackdrop"]';

/** Namensmuster für `dead_click` aus batchSchema.ts — ziffernfrei, damit keine Nummer durchkommt. */
const DEAD_CLICK_NAME = /^dead:[A-Za-z_.:>/-]{1,120}$/;
/** Ein Marker darf in den Schlüssel, wenn er nur aus den Zeichen des Musters besteht. */
const SAFE_MARKER = /^[A-Za-z_.:/-]+$/;

/** Erfüllt der Name das Muster, das batchSchema.ts für `dead_click` verlangt? */
export const isSafeDeadClickName = (name: string): boolean => typeof name === 'string' && DEAD_CLICK_NAME.test(name);

/**
 * Tag-Name in Kleinschreibung, auf die Zeichen des Namensmusters beschränkt. Betroffen sind nur
 * Überschriften (h1..h6 → 'h'): das Muster ist ziffernfrei, und die Gliederungsebene gehört
 * nicht zum Ort. Ohne diese Kürzung verlöre jede Überschrift ihre Meldung ganz.
 */
const tagName = (element: Element): string => (element.tagName || '').toLowerCase().replace(/[^a-z-]/g, '');

/**
 * Tag-Stufen von `start` aufwärts, außen→innen. Ende ist der markierte Vorfahr (er steht schon
 * als Marker im Schlüssel), das Dokument selbst ('body'/'html') oder die Stufengrenze.
 */
const tagLevels = (start: Element, stopAt: Element | null, limit: number): string[] => {
  const tags: string[] = [];
  let current: Element | null = start;
  while (current && current !== stopAt && tags.length < limit) {
    const tag = tagName(current);
    if (tag === '' || tag === 'body' || tag === 'html') break;
    tags.push(tag);
    current = current.parentElement;
  }
  return tags.reverse();
};

/** Liegt der Punkt in einem sichtbaren Rechteck? Ein 0×0-Rechteck ist nicht dargestellt. */
const containsPoint = (element: Element, point: DeadClickPoint): boolean => {
  const rect = element.getBoundingClientRect();
  if (!rect || rect.width <= 0 || rect.height <= 0) return false;
  return (
    point.clientX >= rect.left &&
    point.clientX <= rect.right &&
    point.clientY >= rect.top &&
    point.clientY <= rect.bottom
  );
};

/**
 * Deaktiviertes Bedienelement unter dem Klickpunkt — der häufigste echte Totklick. MUI setzt an
 * einem deaktivierten ButtonBase `pointer-events: none`; der Klick trifft dann den Container,
 * das eigentliche Element liegt als NACHFAHR unter dem Zeiger.
 */
const disabledUnderPoint = (start: Element, point: DeadClickPoint): Element | null => {
  const candidates = start.querySelectorAll(DISABLED_SELECTOR);
  const limit = Math.min(candidates.length, MAX_DISABLED_CANDIDATES);
  for (let index = 0; index < limit; index += 1) {
    const candidate = candidates[index];
    if (containsPoint(candidate, point)) return candidate;
  }
  return null;
};

const cursorOf = (element: Element, computedStyle: (el: Element) => { cursor: string }): string => {
  try {
    return computedStyle(element)?.cursor ?? '';
  } catch {
    return '';
  }
};

const defaultComputedStyle = (element: Element): { cursor: string } => getComputedStyle(element);

/**
 * Totklick am Klickpunkt oder null.
 *
 * Vorbedingung (1): der Klick hat KEIN Bedienelement getroffen. Der Aufrufer prüft das mit
 * `resolveClickTarget`; hier steht die Prüfung noch einmal, damit die Funktion auch allein
 * benutzbar ist. Geprüft wird dabei der Bedienelement-Vorfahr direkt und nicht das Ergebnis von
 * `resolveClickTarget`: dieses meldet zusätzlich jeden `[data-usage]`-Vorfahren als Ziel — und
 * genau der ist nach Regel (2c) ein Totklick-Signal und liefert den Anfang des Schlüssels.
 *
 * Totklick (2), wenn mindestens eins gilt: (a) ein deaktiviertes Bedienelement liegt unter dem
 * Punkt, (b) der Zeiger ist eine Hand, (c) der Klick liegt in einem markierten Container.
 * Ausgenommen (3): Eingabefelder, ruhiger Text ohne Zeigerhand und der Dialog-Hintergrund.
 */
export const resolveDeadClick = (
  start: Element | null,
  point: DeadClickPoint,
  { computedStyle = defaultComputedStyle }: DeadClickDeps = {},
): DeadClick | null => {
  if (!start || typeof start.closest !== 'function') return null;
  // (1) Vorbedingung: über dem Klickpunkt liegt kein Bedienelement.
  if (start.closest(INTERACTIVE_SELECTOR)) return null;
  // (3) Ausnahmen — hier ist der Klick entweder Eingabe oder wirksame Bedienung.
  if (start.closest(INPUT_SELECTOR)) return null;
  if (start.closest(BACKDROP_SELECTOR)) return null;

  const marked = start.closest(`[${USAGE_ATTRIBUTE}]`);
  const deaktiviert = disabledUnderPoint(start, point);
  // (2) Sieht es bedienbar aus? Ohne eines der drei Merkmale ist es ruhiger Inhalt.
  if (!deaktiviert && cursorOf(start, computedStyle) !== 'pointer' && !marked) return null;

  const kind = (deaktiviert ? `${elementKind(deaktiviert)}-disabled` : elementKind(start)).slice(0, MAX_KIND_LENGTH);

  const markerWert = (marked?.getAttribute(USAGE_ATTRIBUTE) || '').trim();
  // Ein Marker mit fremden Zeichen käme nicht durch das Namensmuster; statt die Meldung zu
  // verlieren, bleibt dann der reine Tag-Pfad (heute trägt kein Marker solche Zeichen).
  const marker = SAFE_MARKER.test(markerWert) ? markerWert : '';
  const tags = tagLevels(start, marker ? marked : null, marker ? MAX_TAG_LEVELS_WITH_MARKER : MAX_TAG_LEVELS);
  const ort = [marker, ...tags].filter((teil) => teil.length > 0).join('>');
  const key = `dead:${ort}`;
  // Letzte Schranke: was das Namensmuster des Backends verletzt, wird gar nicht gemeldet — ein
  // einziges ungültiges Feld lässt den Server das GANZE Bündel verwerfen.
  if (!isSafeDeadClickName(key)) return null;
  return { key, kind };
};
